import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  TranscriptionDocument,
  TranscriptionDocumentType,
  getDocumentTypeLabel,
  getDocumentStatusLabel,
  getDocumentStatusColor,
  formatFileSize
} from '@/types/transcriptionDocumentTypes';
import {
  getSessionDocuments,
  processSessionDocuments,
  uploadGenericDocument,
  downloadDocument,
  createDocumentPreviewUrl,
  deleteDocument
} from '@/services/transcriptionDocumentService';
import { DocumentPreviewDialog } from '@/components/DocumentPreviewDialog/DocumentPreviewDialog';
import { useAppUser } from '@/contexts/AppUserProvider';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Slider,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  PlayArrowOutlined as PlayArrow,
  PauseOutlined as Pause,
  StopOutlined as Stop,
  DownloadOutlined as Download,
  VolumeUpOutlined as VolumeUp,
  AudiotrackOutlined as Audiotrack,
  GraphicEqOutlined as GraphicEq,
  DescriptionOutlined as Description,
  TextSnippetOutlined as TextSnippet,
  InsightsOutlined as Insights,
  SummarizeOutlined as Summarize,
  AttachFileOutlined as AttachFileIcon,
  DeleteOutlined as DeleteIcon,
  VisibilityOutlined as VisibilityIcon
} from '@mui/icons-material';
import { Waveform, Camera } from '@phosphor-icons/react';

interface TranscriptionDocumentTabProps {
  sessionId: string;
  uploadTrigger?: number;
  isUploading?: boolean;
  onDocumentUploaded?: (documentId: string) => void;
}

export const TranscriptionDocumentTab: React.FC<
  TranscriptionDocumentTabProps
> = ({ sessionId, uploadTrigger, isUploading = false, onDocumentUploaded }) => {
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [documents, setDocuments] = useState<TranscriptionDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingDocument, setPlayingDocument] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] =
    useState<TranscriptionDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewDocument, setPreviewDocument] =
    useState<TranscriptionDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewContentType, setPreviewContentType] = useState<string>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const getTypeIcon = (docType: TranscriptionDocumentType) => {
    switch (docType) {
      case TranscriptionDocumentType.OriginalAudio:
        return <Waveform size={16} weight="regular" />;
      case TranscriptionDocumentType.ProcessedAudio:
        return <Waveform size={16} weight="regular" />;
      case TranscriptionDocumentType.RealtimeTranscript:
        return <TextSnippet sx={{ fontSize: 16 }} />;
      case TranscriptionDocumentType.BatchTranscript:
        return <Description sx={{ fontSize: 16 }} />;
      case TranscriptionDocumentType.AIInsights:
        return <Insights sx={{ fontSize: 16 }} />;
      case TranscriptionDocumentType.Summary:
        return <Summarize sx={{ fontSize: 16 }} />;
      case TranscriptionDocumentType.Attachment:
        return <Description sx={{ fontSize: 16 }} />;
      default:
        return <Description sx={{ fontSize: 16 }} />;
    }
  };

  const refreshDocuments = async () => {
    if (!appUser?.id) return;
    try {
      setLoading(true);
      const docs = await getSessionDocuments(
        sessionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      const sortedDocs = docs.sort((a, b) => {
        const typePriority = {
          [TranscriptionDocumentType.OriginalAudio]: 1,
          [TranscriptionDocumentType.ProcessedAudio]: 2,
          [TranscriptionDocumentType.RealtimeTranscript]: 3,
          [TranscriptionDocumentType.BatchTranscript]: 4,
          [TranscriptionDocumentType.AIInsights]: 5,
          [TranscriptionDocumentType.Summary]: 6,
          [TranscriptionDocumentType.FormattedTranscript]: 7,
          [TranscriptionDocumentType.Attachment]: 8
        } as Record<number, number>;

        const priorityDiff =
          typePriority[a.documentType] - typePriority[b.documentType];
        if (priorityDiff !== 0) return priorityDiff;

        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });

      setDocuments(sortedDocs);
    } catch (err) {
      setError('Failed to load documents');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, appUser?.id, getAccessTokenSilently]);

  // Refresh when upload trigger changes (indicates new upload from parent)
  useEffect(() => {
    if (uploadTrigger !== undefined && uploadTrigger > 0) {
      refreshDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadTrigger]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use rear camera by default
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        handleFilesUpload(files);
      }
    };
    input.click();
  };

  const handleFileAttachment = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        handleFilesUpload(files);
      }
    };
    input.click();
  };

  const handleFilesUpload = async (files: File[]) => {
    if (!appUser?.id) return;

    try {
      setUploading(true);
      setError(null);

      for (const file of files) {
        const result = await uploadGenericDocument(
          sessionId,
          file,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup,
          {
            skipAnalysis: true
          }
        );

        if (!result.success) {
          throw new Error(result.message || `Failed to upload ${file.name}`);
        }

        if (result.document?.id && onDocumentUploaded) {
          onDocumentUploaded(result.document.id);
        }
      }

      await processSessionDocuments(
        sessionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      // Refresh documents list
      await refreshDocuments();
    } catch (err) {
      setError('Failed to upload file');
      console.error('Error uploading file:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!appUser?.id) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    try {
      await handleFilesUpload(files);
    } catch (err) {
      console.error('Upload failed', err);
      setError('Failed to upload document');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  // Parse duration to seconds - handles both ISO 8601 (PT7S) and TimeSpan (00:00:07) formats
  const parseDuration = (duration: string): number => {
    if (!duration || typeof duration !== 'string') {
      console.warn(
        'Invalid duration string provided to parseDuration:',
        duration
      );
      return 0;
    }

    // Try ISO 8601 format first (PT7S, PT2M30.123456S, etc.)
    const iso8601Regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d{1,7})?)S)?/;
    const iso8601Matches = duration.match(iso8601Regex);

    if (iso8601Matches) {
      const hours = parseInt(iso8601Matches[1] || '0', 10);
      const minutes = parseInt(iso8601Matches[2] || '0', 10);
      const seconds = parseFloat(iso8601Matches[3] || '0');

      // Validate parsed values
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        console.warn('Parsed ISO 8601 duration values contain NaN:', {
          hours,
          minutes,
          seconds
        });
        return 0;
      }

      const totalSeconds = hours * 3600 + minutes * 60 + seconds;

      if (!isFinite(totalSeconds) || totalSeconds < 0) {
        console.warn('Calculated ISO 8601 duration is invalid:', totalSeconds);
        return 0;
      }

      return totalSeconds;
    }

    // Try TimeSpan format with full precision support
    // Supports: HH:MM:SS, HH:MM:SS.fffffff, d.HH:MM:SS.fffffff
    const timeSpanRegex =
      /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,7}))?$/;
    const timeSpanMatches = duration.match(timeSpanRegex);

    if (timeSpanMatches) {
      const days = timeSpanMatches[1] ? parseInt(timeSpanMatches[1], 10) : 0;
      const hours = parseInt(timeSpanMatches[2], 10);
      const minutes = parseInt(timeSpanMatches[3], 10);
      const seconds = parseInt(timeSpanMatches[4], 10);

      // Handle fractional seconds with full precision (up to 7 decimal places)
      let fractionalSeconds = 0;
      if (timeSpanMatches[5]) {
        const fractionalStr = timeSpanMatches[5];
        // Convert to actual decimal by dividing by 10^(number of digits)
        fractionalSeconds =
          parseInt(fractionalStr, 10) / Math.pow(10, fractionalStr.length);
      }

      // Validate parsed values
      if (
        isNaN(days) ||
        isNaN(hours) ||
        isNaN(minutes) ||
        isNaN(seconds) ||
        isNaN(fractionalSeconds)
      ) {
        return 0;
      }

      const totalSeconds =
        days * 86400 +
        hours * 3600 +
        minutes * 60 +
        seconds +
        fractionalSeconds;

      if (!isFinite(totalSeconds) || totalSeconds < 0) {
        return 0;
      }

      return totalSeconds;
    }

    return 0;
  };

  const handlePlayAudio = async (document: TranscriptionDocument) => {
    if (playingDocument === document.id) {
      // Toggle pause/resume for current document
      if (audioRef.current) {
        if (isPaused) {
          // Resume playback
          audioRef.current.play();
          setIsPaused(false);
        } else {
          // Pause playback
          audioRef.current.pause();
          setIsPaused(true);
        }
      }
      return;
    } else {
      // Start new playback
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Reset pause state when switching to a different document
      setIsPaused(false);

      try {
        // Get token with proper auth params (matching other services)
        const authParams = {
          authorizationParams: {
            audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
          }
        };

        let token: string;
        try {
          token = await getAccessTokenSilently(authParams);
        } catch (error: any) {
          if (
            error.error === 'consent_required' ||
            error.error === 'login_required' ||
            error.error === 'interaction_required'
          ) {
            const popupToken = await getAccessTokenWithPopup(authParams);
            if (!popupToken) {
              throw new Error('Failed to get access token');
            }
            token = popupToken as string;
          } else {
            throw error;
          }
        }

        // Create authenticated blob URL for audio streaming
        const response = await fetch(
          `${
            import.meta.env.VITE_API_BASE_URL || '/api'
          }/transcriptionsessions/${sessionId}/stream/${document.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'X-User-Id': appUser?.id || ''
            }
          }
        );

        if (!response.ok) {
          throw new Error(
            `Audio stream failed: ${response.status} - ${response.statusText}. This may be due to Azure Blob Storage access restrictions.`
          );
        }

        const blob = await response.blob();
        const audioUrl = URL.createObjectURL(blob);

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        // Use stored duration immediately if available
        if (document.duration) {
          const parsedDuration = parseDuration(document.duration);
          if (parsedDuration > 0) {
            setDuration(parsedDuration);
          }
        }

        audio.onloadedmetadata = () => {
          const audioDuration = audio.duration;
          if (!duration && isFinite(audioDuration) && audioDuration > 0) {
            setDuration(audioDuration);
          }
        };

        audio.onloadeddata = () => {
          const audioDuration = audio.duration;
          if (!duration && isFinite(audioDuration) && audioDuration > 0) {
            setDuration(audioDuration);
          }
        };

        audio.ondurationchange = () => {
          const audioDuration = audio.duration;
          if (!duration && isFinite(audioDuration) && audioDuration > 0) {
            setDuration(audioDuration);
          }
        };

        audio.ontimeupdate = () => {
          setCurrentTime(audio.currentTime);
        };

        audio.onended = () => {
          setPlayingDocument(null);
          setIsPaused(false);
          setCurrentTime(0);
          URL.revokeObjectURL(audioUrl); // Clean up blob URL
        };
        audio.onerror = () => {
          setPlayingDocument(null);
          setIsPaused(false);
          setCurrentTime(0);
          setDuration(0);
          URL.revokeObjectURL(audioUrl); // Clean up blob URL
        };

        audio.play();
        setPlayingDocument(document.id);
      } catch (error) {
        setPlayingDocument(null);
        setIsPaused(false);
        setCurrentTime(0);
        setDuration(0);
      }
    }
  };

  const handleStopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingDocument(null);
    setIsPaused(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const handleSeek = (_event: Event, newValue: number | number[]) => {
    if (audioRef.current && isFinite(duration) && duration > 0) {
      audioRef.current.currentTime = newValue as number;
      setCurrentTime(newValue as number);
    }
  };

  // Add polling for duration if we don't have a valid duration
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (
      playingDocument &&
      audioRef.current &&
      (!duration || duration <= 0 || isNaN(duration) || !isFinite(duration))
    ) {
      interval = setInterval(() => {
        if (audioRef.current) {
          const audioDuration = audioRef.current.duration;
          if (isFinite(audioDuration) && audioDuration > 0) {
            setDuration(audioDuration);
            if (interval) clearInterval(interval);
          }
        }
      }, 500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [playingDocument, duration]);

  // Cleanup effect to stop audio playback when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setPlayingDocument(null);
      setIsPaused(false);
      setCurrentTime(0);
      setDuration(0);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading documents...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  const isAudioDocument = (doc: TranscriptionDocument) => {
    return (
      doc.documentType === TranscriptionDocumentType.OriginalAudio ||
      doc.documentType === TranscriptionDocumentType.ProcessedAudio
    );
  };

  const handleDownload = async (document: TranscriptionDocument) => {
    try {
      await downloadDocument(
        sessionId,
        document.id,
        appUser?.id || '',
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        document.documentName
      );
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const clearPreviewUrl = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handlePreview = async (document: TranscriptionDocument) => {
    if (!appUser?.id) return;

    clearPreviewUrl();
    setPreviewDocument(document);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewContentType(undefined);

    try {
      const preview = await createDocumentPreviewUrl(
        sessionId,
        document.id,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setPreviewUrl(preview.url);
      setPreviewContentType(preview.contentType);
    } catch (error) {
      console.error('Error previewing document:', error);
      setPreviewError('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    clearPreviewUrl();
    setPreviewDocument(null);
    setPreviewContentType(undefined);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const handleDeleteClick = (document: TranscriptionDocument) => {
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete || !appUser?.id) return;

    try {
      setIsDeleting(true);
      const result = await deleteDocument(
        sessionId,
        documentToDelete.id,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      if (result.success) {
        // Refresh documents list
        await refreshDocuments();
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };

  const canDeleteDocument = (doc: TranscriptionDocument) => {
    // Cannot delete OriginalAudio documents
    return doc.documentType !== TranscriptionDocumentType.OriginalAudio;
  };

  const formatFileFormat = (fileFormat?: string): string | null => {
    if (!fileFormat) return null;
    // Remove leading dot if present and uppercase
    return fileFormat.replace(/^\./, '').toUpperCase();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Hidden file input for upload functionality */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.txt,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      />

      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Upload Loading Indicator */}
        {(isUploading || uploading) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              mb: 2,
              backgroundColor: '#f0f9ff',
              border: '1px solid #60a5fa',
              borderRadius: '10px'
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Uploading document...
            </Typography>
          </Box>
        )}

        {documents.length === 0 && !isUploading && !uploading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px'
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', fontSize: '14px', maxWidth: '300px' }}
            >
              No documents uploaded yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {documents.map((doc) => (
              <Box
                key={doc.id}
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1.5 : 2,
                  p: 2,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.02)' 
                      : '#fafafa'
                  },
                  transition: 'all 0.15s ease'
                }}
              >
                {/* Header Row - Icon + Type + Status */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 1.5 : 2,
                    width: '100%'
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : '#f3f4f6',
                      color: '#6b7280',
                      flexShrink: 0
                    }}
                    aria-hidden
                  >
                    {getTypeIcon(doc.documentType)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Title */}
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: 1.4,
                        mb: 0.5,
                        color: theme.palette.text.primary,
                        textAlign: 'left'
                      }}
                      title={getDocumentTypeLabel(doc.documentType)}
                    >
                      {getDocumentTypeLabel(doc.documentType)}
                    </Typography>

                    {/* Metadata - Single line with bullets */}
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: theme.palette.text.secondary,
                        textAlign: 'left'
                      }}
                    >
                      {[
                        formatFileFormat(doc.fileFormat),
                        doc.fileSizeBytes !== undefined
                          ? formatFileSize(doc.fileSizeBytes)
                          : null,
                        isAudioDocument(doc) && doc.duration && `Recording: ${formatTime(parseDuration(doc.duration))}`,
                        new Date(doc.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      ].filter(Boolean).join(' • ')}
                      {doc.documentStatus !== 2 && ` • ${getDocumentStatusLabel(doc.documentStatus)}`}
                    </Typography>
                  </Box>
                </Box>

                {/* Audio Player Controls - Full Width on Mobile */}
                {playingDocument === doc.id && (
                  <Box sx={{ width: '100%', mt: isMobile ? 1 : 0 }}>
                    {isFinite(duration) && duration > 0 ? (
                      <>
                        <Slider
                          value={currentTime}
                          min={0}
                          max={duration}
                          onChange={handleSeek}
                          aria-labelledby="audio-slider"
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '11px' }}
                          >
                            {formatTime(currentTime)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '11px' }}
                          >
                            {formatTime(duration)}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                            opacity: 0.7
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '11px' }}
                          >
                            {formatTime(currentTime)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '11px' }}
                          >
                            Loading...
                          </Typography>
                        </Box>
                        <LinearProgress variant="indeterminate" />
                      </>
                    )}
                  </Box>
                )}

                {/* Action Buttons - Full Width Row on Mobile */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexShrink: 0,
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: isMobile ? 'flex-start' : 'flex-end'
                  }}
                >
                  {isAudioDocument(doc) && (
                    <>
                      {isMobile ? (
                        // Mobile: Icon-only button
                        <IconButton
                          onClick={() => handlePlayAudio(doc)}
                          size="small"
                          sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            color: '#6b7280',
                            width: 36,
                            height: 36,
                            '&:hover': {
                              backgroundColor: '#f9fafb',
                              borderColor: '#d1d5db'
                            }
                          }}
                        >
                          {playingDocument === doc.id && !isPaused ? (
                            <Pause fontSize="small" />
                          ) : (
                            <PlayArrow fontSize="small" />
                          )}
                        </IconButton>
                      ) : (
                        // Desktop: Full button with text
                        <Button
                          variant="outlined"
                          onClick={() => handlePlayAudio(doc)}
                          size="small"
                          startIcon={
                            playingDocument === doc.id && !isPaused ? (
                              <Pause />
                            ) : (
                              <PlayArrow />
                            )
                          }
                          sx={{
                            borderRadius: '6px',
                            borderColor: '#e5e7eb',
                            color: '#6b7280',
                            minWidth: 'auto',
                            px: 1.5,
                            fontSize: '12px',
                            '&:hover': {
                              backgroundColor: '#f9fafb',
                              borderColor: '#d1d5db'
                            }
                          }}
                        >
                          {playingDocument === doc.id && !isPaused
                            ? 'Pause'
                            : 'Play'}
                        </Button>
                      )}
                    </>
                  )}
                  {!isAudioDocument(doc) &&
                    (isMobile ? (
                      <IconButton
                        onClick={() => handlePreview(doc)}
                        size="small"
                        aria-label={`Preview ${doc.documentName}`}
                        sx={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          color: '#6b7280',
                          width: 36,
                          height: 36,
                          '&:hover': {
                            backgroundColor: '#f9fafb',
                            borderColor: '#d1d5db'
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => handlePreview(doc)}
                        size="small"
                        startIcon={<VisibilityIcon />}
                        sx={{
                          borderRadius: '6px',
                          borderColor: '#e5e7eb',
                          color: '#6b7280',
                          minWidth: 'auto',
                          px: 1.5,
                          fontSize: '12px',
                          '&:hover': {
                            backgroundColor: '#f9fafb',
                            borderColor: '#d1d5db'
                          }
                        }}
                      >
                        Preview
                      </Button>
                    ))}
                  {isMobile ? (
                    // Mobile: Icon-only button
                    <IconButton
                      onClick={() => handleDownload(doc)}
                      size="small"
                      sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        color: '#6b7280',
                        width: 36,
                        height: 36,
                        '&:hover': {
                          backgroundColor: '#f9fafb',
                          borderColor: '#d1d5db'
                        }
                      }}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  ) : (
                    // Desktop: Full button with text
                    <Button
                      variant="outlined"
                      onClick={() => handleDownload(doc)}
                      size="small"
                      startIcon={<Download />}
                      sx={{
                        borderRadius: '6px',
                        borderColor: '#e5e7eb',
                        color: '#6b7280',
                        minWidth: 'auto',
                        px: 1.5,
                        fontSize: '12px',
                        '&:hover': {
                          backgroundColor: '#f9fafb',
                          borderColor: '#d1d5db'
                        }
                      }}
                    >
                      Download
                    </Button>
                  )}
                  {canDeleteDocument(doc) &&
                    (isMobile ? (
                      // Mobile: Icon-only button
                      <IconButton
                        onClick={() => handleDeleteClick(doc)}
                        size="small"
                        sx={{
                          border: '1px solid #fee2e2',
                          borderRadius: '6px',
                          color: '#dc2626',
                          width: 36,
                          height: 36,
                          '&:hover': {
                            backgroundColor: '#fef2f2',
                            borderColor: '#fecaca'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      // Desktop: Full button with text
                      <Button
                        variant="outlined"
                        onClick={() => handleDeleteClick(doc)}
                        size="small"
                        startIcon={<DeleteIcon />}
                        sx={{
                          borderRadius: '6px',
                          borderColor: '#fee2e2',
                          color: '#dc2626',
                          minWidth: 'auto',
                          px: 1.5,
                          fontSize: '12px',
                          '&:hover': {
                            backgroundColor: '#fef2f2',
                            borderColor: '#fecaca'
                          }
                        }}
                      >
                        Delete
                      </Button>
                    ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <DocumentPreviewDialog
        open={Boolean(previewDocument)}
        title={previewDocument?.documentName || 'Document preview'}
        previewUrl={previewUrl}
        contentType={previewContentType}
        fileFormat={previewDocument?.fileFormat}
        loading={previewLoading}
        error={previewError}
        onClose={handlePreviewClose}
        onDownload={
          previewDocument ? () => handleDownload(previewDocument) : undefined
        }
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete "{documentToDelete?.documentName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranscriptionDocumentTab;
